import type { RfqPackage, TimberSupplierInvite, TimberSupplierStructureRef } from '../types';
import { inviteHasStructureScope } from './timberRfqStructureScope';
import { structureSpeciesChangeLines, structureSpeciesEqual } from './timberRfqStructureSpecies';
import {
  packageScopeStructureNames,
  type RfqPackageScopeFields,
} from './rfqPackageScopeNames';

/**
 * Detects when a supplier's structure selection (on the RFQ project invite) has
 * drifted from what their already-published package covers, so staff can resend
 * a scope-change notice. Matching mirrors timberRfqStructureScope: names are the
 * stable cross-source key (hub-CSV releases regenerate structure ids).
 */

type ScopeFields = RfqPackageScopeFields & Pick<RfqPackage, 'structureSpecies'>;
type InviteScopeFields = Pick<TimberSupplierInvite, 'structureScope' | 'structures' | 'structureSpecies' | 'requestedSpecies'>;

/**
 * True when the invite's species-per-structure no longer match what the package
 * snapshotted (timber only). An absent map on either side means "every species".
 */
export function packageStructureSpeciesDiffersFromInvite(
  pkg: Pick<RfqPackage, 'structureSpecies' | 'workType'> & Partial<ScopeFields>,
  invite: InviteScopeFields | undefined,
): boolean {
  if ((pkg.workType ?? 'timber_rfq') !== 'timber_rfq') return false;
  const scopeIds = packageStructureIdsForSpecies(pkg, invite);
  return !structureSpeciesEqual(
    restrictToIds(pkg.structureSpecies, scopeIds),
    restrictToIds(invite?.structureSpecies, scopeIds),
    invite?.requestedSpecies ?? [],
  );
}

/** Every id/name pair we know for structures touching this package or invite. */
function knownStructureRefs(pkg: Partial<ScopeFields>, invite: InviteScopeFields | undefined): TimberSupplierStructureRef[] {
  return [
    ...(invite?.structures ?? []),
    ...(pkg.scopeStructures ?? []),
    ...((pkg as Pick<RfqPackage, 'proposalSnapshot'>).proposalSnapshot?.nodeSets ?? []).map(ns => ({ id: ns.id, name: ns.name })),
  ];
}

/**
 * Ids of the structures this package covers (null = package is unscoped, so
 * every structure counts). A batched request round splits one invite across
 * several packages, so a species change on Sale Barn must not flag the package
 * that only covers Equipment Barn.
 */
function packageStructureIdsForSpecies(pkg: Partial<ScopeFields>, invite: InviteScopeFields | undefined): Set<string> | null {
  const pkgNames = toNameSet(packageScopeStructureNames(pkg as ScopeFields));
  if (!pkgNames.size) return null;
  const ids = new Set<string>();
  for (const ref of knownStructureRefs(pkg, invite)) {
    if (ref.id && pkgNames.has(normalizeName(ref.name))) ids.add(ref.id);
  }
  return ids;
}

function restrictToIds(
  map: Record<string, string[]> | undefined,
  ids: Set<string> | null,
): Record<string, string[]> | undefined {
  if (!map || !ids) return map;
  const out: Record<string, string[]> = {};
  for (const [id, species] of Object.entries(map)) {
    if (ids.has(id)) out[id] = species;
  }
  return out;
}

function normalizeName(name: string | undefined): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export { packageScopeStructureNames };

/** True when the package doc records an explicit structure scope. */
function packageHasRecordedScope(pkg: ScopeFields): boolean {
  return !!pkg.scopeStructures?.length || !!pkg.cncPackagePayload?.structures?.length;
}

function toNameSet(names: string[]): Set<string> {
  const out = new Set<string>();
  for (const name of names) {
    const key = normalizeName(name);
    if (key) out.add(key);
  }
  return out;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const key of Array.from(a)) {
    if (!b.has(key)) return false;
  }
  return true;
}

/**
 * True when the supplier's current invite selection no longer matches what their
 * published package covers.
 *
 * `totalStructureCount` (enabled structures on the proposal) disambiguates the
 * timber legacy fallback: an unscoped legacy package lists every structure in
 * its snapshot, which should not count as drift against an unscoped invite.
 */
export function packageScopeDiffersFromInvite(
  pkg: ScopeFields & Pick<RfqPackage, 'workType'>,
  invite: InviteScopeFields | undefined,
  totalStructureCount: number,
): boolean {
  if (packageStructureSpeciesDiffersFromInvite(pkg, invite)) return true;
  return packageStructureScopeDiffersFromInvite(pkg, invite, totalStructureCount);
}

/** Structure-only drift (ignores species). */
export function packageStructureScopeDiffersFromInvite(
  pkg: ScopeFields,
  invite: InviteScopeFields | undefined,
  totalStructureCount: number,
): boolean {
  const inviteScoped = inviteHasStructureScope(invite);
  const inviteNames = toNameSet((invite?.structures ?? []).map(s => s.name));
  const pkgNames = toNameSet(packageScopeStructureNames(pkg));
  const pkgScoped =
    packageHasRecordedScope(pkg)
    || (pkgNames.size > 0 && totalStructureCount > 0 && pkgNames.size < totalStructureCount);

  if (!pkgScoped) {
    // Package covers all structures.
    if (!inviteScoped) return false;
    // A scoped invite that names every structure is effectively "all" too.
    return totalStructureCount > 0 ? inviteNames.size < totalStructureCount : true;
  }
  // Package is scoped; an unscoped invite now covers more than it did.
  if (!inviteScoped) return true;
  return !setsEqual(inviteNames, pkgNames);
}

export type ScopeChangeSummary = {
  /** Structures on the invite now that the package does not cover. */
  added: TimberSupplierStructureRef[];
  /** Structure names the package covers that are no longer on the invite. */
  removed: string[];
  /** Per-structure species changes, e.g. "Sale Barn — Glulam only (was Glulam, Pine)". */
  speciesChanges: string[];
};

/** Added/removed structures between the published package and the invite's current selection. */
export function scopeChangeSummary(
  pkg: ScopeFields,
  invite: InviteScopeFields | undefined,
): ScopeChangeSummary {
  const pkgNames = packageScopeStructureNames(pkg);
  const pkgSet = toNameSet(pkgNames);
  const inviteRefs = inviteHasStructureScope(invite) ? invite!.structures! : [];
  const inviteSet = toNameSet(inviteRefs.map(s => s.name));

  const added = inviteRefs.filter(ref => !pkgSet.has(normalizeName(ref.name)));
  const removed = inviteSet.size
    ? pkgNames.filter(name => !inviteSet.has(normalizeName(name)))
    : [];
  const structureRefs = knownStructureRefs(pkg, invite);
  const scopeIds = packageStructureIdsForSpecies(pkg, invite);
  const speciesChanges = structureSpeciesChangeLines(
    restrictToIds(pkg.structureSpecies, scopeIds),
    restrictToIds(invite?.structureSpecies, scopeIds),
    invite?.requestedSpecies ?? [],
    structureRefs,
  );
  return { added, removed, speciesChanges };
}
